export type SampleCategory = {
  title: string;
  questions: string[];
};

export const sampleCategories: SampleCategory[] = [
  {
    title: "General Concepts and Scope",
    questions: [
      "What is the main objective of these regulations?",
      "How has the definition of a medical device been updated under the MDR?",
      "Under what circumstances does software (SaMD) fall within the scope of the MDR?",
      "Are accessories considered medical devices?",
      "From what date did the MDR fully enter into force?",
      "Why is the Intended Purpose critical for documentation?",
      "What is the biggest difference between the MDR and the previous directive (MDD)?",
      "What are the exceptions for custom-made devices?",
    ],
  },
  {
    title: "Device Classification",
    questions: [
      "Into which risk classes are medical devices divided under the MDR?",
      "What is the conformity assessment route for Class IIb medical devices?",
      "Which Annex is mandatory for Class III devices?",
      "Is the manufacturer's self-declaration sufficient for Class I devices?",
      "Which rules differ for sterile Class I devices?",
      "Is a Notified Body required for Class I devices with a measuring function?",
      "What are the key criteria that move a device from Class I to Class IIa?",
      "What are the classification rules for invasive devices?",
      "Which class do active medical devices fall into?",
      "Is there a special classification rule for devices containing nanomaterials?",
      "How are drug-device combinations under Article 117 classified?",
      "Under which Annex are the classification rules located?",
      "What is the special case of reusable surgical instruments (Class Ir)?",
      "What are the details of Rule 11 used for software classification?",
      "Which class do implantable devices fall into under the MDR?",
    ],
  },
  {
    title: "Technical Documentation (Annex II / Annex III)",
    questions: [
      "What are the main headings that must be present in a technical file under Annex II?",
      "What technical details must be included in the device description and specification section?",
      "What must the information supplied by the manufacturer (labelling and instructions for use) contain?",
      "How detailed must the design and manufacturing information be in the documentation?",
      "How is an equivalent device comparison presented in the technical file?",
      "For how long must a product's technical file be retained?",
      "What does the Post-Market Surveillance file under Annex III contain?",
      "How are variants and configurations defined in the technical file?",
      "At what intervals should the currency of the technical documentation be reviewed?",
      "What do Notified Bodies pay the most attention to in the technical file?",
      "How are raw material contents and biocompatibility data added to the technical file?",
      "How are software architecture and algorithm validation data included in the documentation?",
      "How should sterilization validation data be presented in the technical file?",
      "In which section are shelf-life and stability test results located?",
      "What is the importance of packaging and transport tests in the technical file?",
      "How are manufacturing sites and supplier information indicated in the file?",
      "How is the List of Applied Standards prepared?",
      "What does the regulation say about the language of the technical file?",
      "How are critical components defined in the technical file?",
      "Can a single technical file be prepared for multiple product groups?",
    ],
  },
  {
    title: "GSPR and Annex I",
    questions: [
      "What is the GSPR (Annex I) and why must it be completed?",
      "How are non-applicable clauses justified in the GSPR table?",
      "How are risk management principles addressed in the GSPR?",
      "In which section of the GSPR are chemical, physical and biological properties?",
      "What are the requirements relating to infection and microbial contamination?",
      "What are the electrical safety and electromagnetic compatibility clauses?",
      "What is required in the GSPR for devices with a measuring function?",
      "What are the design-against-user-error (usability) requirements?",
      "What is the relationship between the GSPR and harmonized standards?",
      "What information must the instructions for use (IFU) contain?",
      "What are the labelling obligations?",
    ],
  },
  {
    title: "Risk Management and Usability",
    questions: [
      "How does the ISO 14971 standard align with the MDR?",
      "How is the benefit-risk analysis reported in the documentation?",
      "How is the acceptable risk level determined?",
      "Which phases of the product life cycle does the risk management process cover?",
      "How is usability engineering (IEC 62366) demonstrated in the technical file?",
      "How is the difference between use error and abnormal use handled in the risk file?",
      "How should residual risks be communicated to the user?",
      "What is the hierarchy of risk control measures?",
      "How should software risk analysis (including cybersecurity) be performed?",
      "Should risks in the manufacturing process be included in the technical file?",
      "Where do supplier audit processes sit within the Quality Management System?",
      "How should the manufacturer monitor supplier performance?",
    ],
  },
  {
    title: "Quality Management System (QMS)",
    questions: [
      "How is the Quality Management System (QMS) defined in the MDR?",
      "What elements does the QMS documentation include?",
      "How do ISO 13485 and the MDR QMS requirements overlap?",
      "What are the most common nonconformities encountered during a QMS audit?",
      "How is the PMS (Post-Market Surveillance) system integrated with the QMS?",
    ],
  },
  {
    title: "Clinical Evaluation and Performance",
    questions: [
      "What is a Clinical Evaluation Report (CER)?",
      "How is the level of clinical evidence determined?",
      "When is a clinical investigation mandatory?",
      "What is the purpose of a PMCF (Post-Market Clinical Follow-up) plan?",
      "What is included in the definition of clinical data?",
      "How is a literature review used in clinical evaluation?",
      "What should the qualifications of the clinical evaluation expert be?",
      "How frequently should the CER and PER be updated?",
      "How does the clinical data collection process work for existing (legacy) devices?",
      "How is the ethics committee approval process defined in clinical investigations?",
      "How are diagnostic specificity and sensitivity data included in the PER?",
      "What should be done if the clinical evaluation results in insufficient data?",
      "What is the clinical evaluation requirement under Article 61?",
      "Under what conditions is it possible to submit clinical data via equivalence?",
      "For which devices is the SSCP (Summary of Safety and Clinical Performance) mandatory?",
      "How are reports on clinical investigations uploaded to EUDAMED?",
      "What is the level of clinical data for Class III devices?",
    ],
  },
  {
    title: "Traceability — UDI and EUDAMED",
    questions: [
      "What is the UDI (Unique Device Identification) system?",
      "What is the difference between UDI-DI and UDI-PI?",
      "What is the Basic UDI-DI and where is it used?",
      "Which modules does the EUDAMED database consist of?",
      "When does the obligation for manufacturers to register in EUDAMED begin?",
      "How must the UDI carrier appear on the device label?",
      "How is an SRN (Single Registration Number) obtained?",
      "How is the UDI system applied to implantable devices?",
      "How is a UDI assigned to software?",
      "What information is publicly available through EUDAMED?",
      "From when is the UDI mandatory for Class III devices?",
      "When does the UDI come into effect for Class I devices?",
      "How is the UDI managed for reprocessed single-use devices?",
      "What are the interim solutions until EUDAMED becomes fully functional?",
    ],
  },
  {
    title: "Economic Operators and Responsibilities",
    questions: [
      "What are the manufacturer's main obligations under Article 10?",
      "What does an Authorized Representative do?",
      "In which cases does the Authorized Representative's joint liability arise?",
      "What are the duties of importers and distributors under the regulation?",
      "What are the distributor's storage and transport responsibilities?",
      "In which case is a distributor deemed to have assumed the manufacturer's responsibilities?",
      "For how many years must manufacturers retain the technical documentation?",
      "Is the retention period different for implantable devices?",
      "How is financial coverage (Article 10.16) determined?",
      "What is the procedure for appointing an Authorized Representative for non-EU manufacturers?",
    ],
  },
  {
    title: "Person Responsible for Regulatory Compliance (PRRC)",
    questions: [
      "What is the PRRC and in which article is it defined?",
      "What qualifications are required for the PRRC?",
      "Must Authorized Representatives of non-EU manufacturers have a PRRC?",
      "How is a PRRC appointed in small and micro enterprises?",
      "What is the role of the PRRC in EUDAMED?",
    ],
  },
  {
    title: "Post-Market Surveillance and Vigilance",
    questions: [
      "What is the Post-Market Surveillance (PMS) system?",
      "Which events must be reported under the vigilance system?",
      "What are the reporting timelines for serious incidents?",
      "What is trend reporting?",
      "What is an FSCA (Field Safety Corrective Action)?",
      "How is CAPA (Corrective/Preventive Action) related to PMS?",
      "For which devices is the PSUR (Periodic Safety Update Report) prepared?",
      "How frequently should the PSUR be updated?",
      "What is the SSCP (Summary of Safety and Clinical Performance) report?",
    ],
  },
  {
    title: "Notified Bodies and Auditing",
    questions: [
      "What are the duties of Notified Bodies?",
      "How do the selection and audit process of a Notified Body work?",
      "What is the procedure for unannounced audits?",
      "How is the impartiality of Notified Bodies ensured?",
      "How frequently are Notified Body audits performed?",
      "What happens to devices if a Notified Body is withdrawn?",
      "How is the re-assessment of Notified Bodies carried out?",
    ],
  },
  {
    title: "CE Marking and Certification",
    questions: [
      "What must the content of a CE Declaration of Conformity include?",
      "How long is a CE certificate valid?",
      "Under which article is the CE mark affixed?",
      "In which annexes are the conformity assessment procedures defined?",
      "What is the Annex IX (full QMS) route?",
      "When is the Annex X (type examination) route preferred?",
      "For which devices is the Annex XI (product verification) route used?",
      "When is the Notified Body number added next to the CE mark?",
      "What sanctions apply in the event of regulatory noncompliance?",
    ],
  },
  {
    title: "Software Medical Devices",
    questions: [
      "How is software with a medical purpose assessed under the MDR?",
      "Is lifestyle software within the scope of the MDR?",
      "How should software updates be managed?",
      "Where is software cybersecurity addressed in the MDR?",
      "What are the design control requirements for SaMD (Software as a Medical Device)?",
    ],
  },
  {
    title: "Aesthetic Products (Annex XVI)",
    questions: [
      "Which products does Annex XVI cover?",
      "Are coloured contact lenses subject to the MDR?",
      "What are the MDR obligations for laser hair-removal devices?",
    ],
  },
  {
    title: "UDI Issuing Agencies",
    questions: [
      "What are UDI issuing agencies, and which ones are accredited by the European Commission?",
      "What are the key differences between GS1, HIBCC, ICCBBA and IFA?",
      "For which type of medical device is the HIBCC standard preferred?",
      "For which product groups is the ICCBBA standard considered mandatory (blood, tissue, cells)?",
      "In which sectors other than medical devices is the GS1 standard used?",
      "In which market is IFA (Informationsstelle für Arzneispezialitäten) dominant?",
      "How does a manufacturer choose its UDI issuing agency?",
      "Is it possible to use more than one UDI issuing agency at the same time?",
      "How is the accreditation process of UDI issuing agencies conducted by the European Commission?",
      "How are HIBCC LIC and PCN codes structured?",
      "What is the technical architecture of the ICCBBA ISBT 128 standard?",
      "What are the structural differences between the GS1 GTIN and the HIBCC UPN?",
      "What is done for existing devices in case of a change of UDI issuing agency?",
      "Against which criteria does the EU Commission evaluate UDI issuing agency applications?",
      "Which sections does the UDI issuing agency operating guide contain?",
    ],
  },
  {
    title: "Health Data Standards and Patient Safety",
    questions: [
      "What is the role of data standards in patient safety in the healthcare sector?",
      "What are the contributions of the UDI system to patient safety?",
      "How does medical device traceability affect the clinical error rate?",
      "How does data standardization play a role in device recall processes?",
      "What is the impact of the HIBCC and ICCBBA standards on patient safety?",
      "How is data standardization integrated with pharmacovigilance?",
      "How should UDI scanning processes be set up in healthcare facilities?",
      "What are the minimum elements of medical device data standards for patient safety?",
      "How is UDI data integrated into electronic health records (EHR)?",
      "On which factors does the choice between HIBCC or ICCBBA depend for strategic compliance?",
    ],
  },
];

export const allQuestions: string[] = sampleCategories.flatMap((c) => c.questions);
